import { Input } from "@aix/ui"

interface TrackableEntryInputProps {
  trackable: any
  value: string | number | boolean | null | undefined
  onChange: (value: any) => void
}

export default function TrackableEntryInput({ trackable, value, onChange }: TrackableEntryInputProps) {
  // Number input with unit
  if (trackable.dataType === "number") {
    const numValue = typeof value === "number" ? value : null
    const minValue = trackable.minValue ? parseFloat(trackable.minValue) : undefined
    const maxValue = trackable.maxValue ? parseFloat(trackable.maxValue) : undefined

    // Client-side validation
    const isInvalid =
      numValue !== null &&
      ((minValue !== undefined && numValue < minValue) || (maxValue !== undefined && numValue > maxValue))

    return (
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <Input
            type="number"
            value={typeof value === "number" ? value : ""}
            onChange={(e) => {
              const val = e.target.value
              onChange(val === "" ? null : parseFloat(val))
            }}
            className={`w-40 border-2 ${isInvalid ? "border-destructive" : "border-border"}`}
            placeholder="Enter value"
            step="any"
            min={minValue}
            max={maxValue}
          />
          {trackable.unit && <span className="text-sm font-medium text-muted-foreground">{trackable.unit}</span>}
        </div>
        {/* Validation hints */}
        {(minValue !== undefined || maxValue !== undefined) && (
          <p className="text-xs text-muted-foreground">
            Valid range: {minValue ?? "−∞"} to {maxValue ?? "∞"} {trackable.unit}
          </p>
        )}
        {isInvalid && (
          <p className="text-xs text-destructive font-semibold">
            ⚠️ Value must be between {minValue ?? "−∞"} and {maxValue ?? "∞"}
          </p>
        )}
      </div>
    )
  }

  // Boolean toggle switch
  if (trackable.dataType === "boolean") {
    const isTrue = value === true
    return (
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => onChange(!isTrue)}
          className={`relative inline-flex h-8 w-16 items-center rounded-full transition-colors border-2 border-border ${
            isTrue ? "bg-primary" : "bg-gray-300"
          }`}
        >
          <span
            className={`inline-block h-6 w-6 transform rounded-full bg-background border-2 border-border transition-transform ${
              isTrue ? "translate-x-8" : "translate-x-1"
            }`}
          />
        </button>
        <span className="text-sm font-semibold">{isTrue ? "✅ Yes" : "❌ No"}</span>
      </div>
    )
  }

  // Text input (multiline)
  return (
    <textarea
      value={typeof value === "string" ? value : ""}
      onChange={(e) => onChange(e.target.value || null)}
      className="w-full min-h-[100px] p-3 border-2 border-border bg-background rounded-none resize-y"
      placeholder={trackable.promptText || "Enter text..."}
    />
  )
}
